// Centralized company data for Finovest Holdings Ltd
import {
  Cpu,
  HeartPulse,
  Sprout,
  Building2,
  Atom,
} from "lucide-react";

export const COMPANY = {
  name: "FINOVEST HOLDINGS LTD",
  short: "Finovest",
  tagline: "Capital that compounds. Conviction that endures.",
  logo: "/logo.png",
  description:
    "A private holdings group deploying institutional capital across fintech, healthcare, agriculture, real estate development, and clean energy — anchored in the British Virgin Islands with a strategic hub in Singapore.",
  registration: "BVI Registration No. 1583454",
  founded: "Est. — Private Holdings Group",
  addresses: {
    bvi: {
      label: "Registered Office — British Virgin Islands",
      line: "1072, Main St., Road Town, Tortola VG 1110",
    },
    singapore: {
      label: "Operating Office — Singapore",
      line: "50A Temple Street, Singapore 058595",
    },
  },
  email: "connect@finovestholdings.com",
};

export const PRESENCE = [
  { country: "Singapore", note: "Operating HQ", flag: "🇸🇬" },
  { country: "South Africa", note: "Agriculture, Real Estate", flag: "🇿🇦" },
  { country: "Germany", note: "Healthcare, Fintech", flag: "🇩🇪" },
  { country: "South Korea", note: "Fintech, Healthcare", flag: "🇰🇷" },
  { country: "Hong Kong", note: "Capital Markets", flag: "🇭🇰" },
  { country: "Shanghai", note: "Real Estate, Healthcare", flag: "🇨🇳" },
  { country: "Australia", note: "Banking, Agriculture", flag: "🇦🇺" },
  { country: "Venezuela", note: "Energy-adjacent Real Estate", flag: "🇻🇪" },
];

export const BANKING_PARTNERS = [
  { name: "ANZ", logo: "/logos/anz.svg" },
  { name: "HSBC", logo: "/logos/hsbc.svg" },
  { name: "Citi", logo: "/logos/citi.svg" },
  { name: "Barclays", logo: "/logos/barclays.svg" },
  { name: "Deutsche Bank", logo: "/logos/deutsche.svg" },
  { name: "UBS", logo: "/logos/ubs.svg" },
];

export const SECTORS = [
  {
    key: "fintech",
    title: "Fintech",
    Icon: Cpu,
    summary:
      "Cross-border payment rails, embedded finance, and regulated digital asset infrastructure.",
    detail:
      "We back early-to-growth stage operators building durable financial infrastructure — from payment orchestration and treasury platforms to compliance-grade digital asset custody.",
    theses: [
      "Payment orchestration & FX",
      "Embedded finance for SMEs",
      "Regulated digital asset custody",
    ],
    image: null, // rendered as dark minimalist card
  },
  {
    key: "healthcare",
    title: "Healthcare",
    Icon: HeartPulse,
    summary:
      "Diagnostics platforms, specialty clinical operators, biotech, and chronic-care delivery — as a financial investor.",
    detail:
      "Capital deployed into healthcare operating companies and platform biotech. Finovest invests in — and provides governance and strategic capital to — diagnostics, specialty clinical networks, and chronic-care delivery models. We back operators; we do not run clinical operations ourselves.",
    theses: [
      "Diagnostics & precision medicine platforms",
      "Specialty clinical operating companies",
      "Biotech & chronic-care delivery models",
    ],
    image:
      "https://images.unsplash.com/photo-1639772823849-6efbd173043c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxtZWRpY2FsJTIwbGFib3JhdG9yeSUyMGhlYWx0aGNhcmV8ZW58MHx8fHwxNzgyNDU0OTkwfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    key: "agriculture",
    title: "Agriculture",
    Icon: Sprout,
    summary:
      "Productive land, precision agronomy, and sustainable food-system infrastructure.",
    detail:
      "Long-duration investments in productive farmland, irrigation, precision agronomy, and downstream processing across emerging and frontier geographies.",
    theses: [
      "Productive farmland & water rights",
      "Precision agronomy & drone tech",
      "Cold chain & processing",
    ],
    image: "https://images.pexels.com/photos/12032545/pexels-photo-12032545.jpeg",
  },
  {
    key: "real-estate",
    title: "Real Estate Development",
    Icon: Building2,
    summary:
      "Grade-A commercial, mixed-use, and institutional residential development.",
    detail:
      "We originate and capitalise mixed-use, commercial, and institutional residential projects across Asia-Pacific and emerging markets — held for income and long-term appreciation.",
    theses: [
      "Grade-A commercial development",
      "Mixed-use master planning",
      "Institutional residential",
    ],
    image: "https://images.pexels.com/photos/946310/pexels-photo-946310.jpeg",
  },
  {
    key: "clean-energy",
    title: "Clean Energy",
    Icon: Atom,
    summary:
      "Renewables, grid infrastructure, and next-generation clean nuclear energy projects.",
    detail:
      "Long-duration capital into the energy transition — utility-scale solar, wind, storage, transmission, and emerging clean nuclear (advanced fission, SMRs, and fusion-adjacent technologies).",
    theses: [
      "Utility-scale renewables & storage",
      "Grid & transmission infrastructure",
      "Clean nuclear: SMRs & advanced fission",
    ],
    image: null, // rendered as dark minimalist card
  },
];

export const PORTFOLIO = [
  {
    title: "Cross-Border Payments Platform",
    sector: "Fintech",
    geo: "South Korea · Singapore · Germany · Hong Kong · Shanghai",
    status: "Growth",
    description:
      "Regulated FX and B2B payments rail serving export SMEs across APAC and EMEA corridors.",
  },
  {
    title: "Highveld Farmland Aggregation",
    sector: "Agriculture",
    geo: "South Africa · Venezuela",
    status: "Active",
    description:
      "Aggregated productive farmland with precision agronomy, water-rights consolidation, and grain logistics.",
  },
  {
    title: "Harbour District Mixed-Use",
    sector: "Real Estate",
    geo: "Australia",
    status: "Development",
    description:
      "Grade-A mixed-use waterfront development combining premium residential, hospitality, and retail.",
  },
  {
    title: "Specialty Clinical Operator Investment",
    sector: "Healthcare",
    geo: "Germany",
    status: "Active",
    description:
      "Equity investment in a specialty cardiology and oncology operating company with shared back-office, procurement, and clinical excellence programs.",
  },
  {
    title: "Frontier Energy-Adjacent Real Estate",
    sector: "Real Estate",
    geo: "Venezuela",
    status: "Development",
    description:
      "Strategic land bank and industrial real estate adjacent to energy and logistics corridors.",
  },
  {
    title: "Utility-Scale Solar & Storage Platform",
    sector: "Clean Energy",
    geo: "Australia · South Africa",
    status: "Active",
    description:
      "Originating and capitalising utility-scale solar farms paired with battery storage and grid-services revenue.",
  },
  {
    title: "Advanced Clean Nuclear Initiative",
    sector: "Clean Energy",
    geo: "Germany · Venezuela",
    status: "Development",
    description:
      "Strategic positions in small modular reactor (SMR) and advanced fission programs alongside tier-one engineering partners.",
  },
];

export const TEAM = [
  {
    name: "Harikrishnan",
    title: "Managing Director",
    bio: "A seasoned principal with two decades of global investment and operating experience across Asia-Pacific, EMEA, and emerging markets. Leads firm strategy, allocator relationships, cross-border origination, and oversees Finovest's institutional banking partnerships from Singapore.",
    image:
      "/team/harikrishnan.jpg",
  },
  {
    name: "Senthil Kumar",
    title: "Investment Head — Finovest Holdings Ltd",
    bio: "Investment Head of Finovest Holdings Ltd and Director of Finvest Holding Pte Ltd, the firm's Singapore operating entity. Leads cross-border deal origination, capital structuring, and platform buy-and-build across APAC and EMEA — combining institutional banking discipline with hands-on operating insight.",
    image:
      "/team/senthil-kumar.jpg",
  },
  {
    name: "Lucas Schmidt",
    title: "Head of Healthcare — Sector Partner, Germany",
    bio: "Mr. Schmidt leads Finovest's healthcare investments from Germany. He oversees capital allocation into diagnostics platforms, specialty clinical operators, biotech ventures, and chronic-care delivery models across Europe and APAC. Finovest backs healthcare operators as a financial investor — providing capital, governance, and strategic support — without running clinical operations directly.",
    image:
      "/team/lucas-schmidt.jpg",
  },
  {
    name: "Min Jun Park",
    title: "Head of Fintech — Sector Partner, South Korea",
    bio: "Mr. Park leads Finovest's fintech investments across the APAC corridor and concurrently serves as Executive Director at Nova Financials Korea in Seoul. A regulated-markets veteran, he brings deep operating experience in cross-border payments, digital asset custody, and embedded finance — and provides on-the-ground governance for portfolio companies operating between Seoul, Singapore, and Hong Kong.",
    image:
      "/team/min-jun-park.jpg",
  },
  {
    name: "Aiden Walsh",
    title: "Head of Real Estate — Sector Partner, Australia",
    bio: "Mr. Walsh leads Finovest's real estate investments from Australia, with a focus on Grade-A commercial, mixed-use master-planned, and institutional residential developments across Asia-Pacific. He brings two decades of origination, capital structuring, and value-creation experience — capitalising on long-cycle assets held for income and appreciation.",
    image:
      "/team/aiden-walsh.jpg",
  },
  {
    name: "Samson Nzimande",
    title: "Head of Agriculture — Sector Partner, South Africa",
    bio: "Mr. Nzimande leads Finovest's agriculture investments from South Africa, overseeing productive farmland aggregation, water-rights consolidation, precision agronomy, and downstream processing. A respected operator across the Southern African agri-value chain, he brings decades of on-the-ground experience structuring resilient, long-duration food and farm-infrastructure assets.",
    image:
      "/team/samson-nzimande.jpg",
  },
  {
    name: "Guzman",
    title: "Head of Clean Energy — Sector Partner, Germany · Venezuela",
    bio: "Mr. Guzman leads Finovest's clean energy investments across two strategic geographies — Germany and Venezuela — covering utility-scale renewables, grid and transmission infrastructure, and advanced clean nuclear programs including small modular reactors (SMRs). He bridges European engineering depth with frontier-market origination, structuring long-duration energy-transition assets.",
    image:
      "/team/guzman.jpg",
  },
  {
    name: "Firoz Sha",
    title: "Regional Head — Business Development, Middle East & Asia",
    bio: "Mr. Sha leads Finovest's business development across the Middle East and Asia, building relationships with family offices, regional allocators, and operating partners. He drives origination of cross-border opportunities and supports portfolio engagement throughout the GCC, South Asia, and Southeast Asian corridors.",
    image:
      "/team/firoz-sha.jpg",
  },
];

export const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/team", label: "Team" },
  { to: "/contact", label: "Contact" },
];
